/**
 * #513 — el stock que necesita el PREVIEW de aprobación, en una sola query.
 *
 * Las dos pantallas que aprueban solicitudes (`MaterialRequestsInbox` y
 * `SupervisorDashboard`) muestran una tanda de solicitudes juntas. Se juntan
 * TODOS los `inventory_id` de todos los ítems y se leen de un saque con
 * `.in('id', ids)` — la misma `fetchCurrentStock` que usa la aprobación, no una
 * copia (ver el header de `utils/materialSplitPreview.js`).
 *
 * Los tres estados que el consumidor tiene que poder distinguir:
 *   - `isLoading`  → todavía no sabemos: mostrar "calculando", NUNCA un número.
 *   - `error`      → no se pudo leer: `stockById` queda `null`, el plan cae al
 *                    ruteo viejo y cada ítem sale marcado `sin confirmar`.
 *   - listo        → `stockById` es un Map; un id AUSENTE del Map (fila fuera de
 *                    RLS) también es "no sé" para ese ítem, y `planMaterialSplit`
 *                    ya lo trata así.
 *
 * @module hooks/useMaterialSplitPreview
 */

import { useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchCurrentStock } from '../services/materialRequestService';
import { collectInventoryIds, summarizeSplitPreview } from '../utils/materialSplitPreview';

/** Map estable: sin ítems de catálogo no hay nada que preguntar (≠ desconocido). */
const EMPTY_STOCK = new Map();

/**
 * @param {Array<object>} requests - filas de `v_material_requests_with_items`
 * @returns {{stockById: Map<string, number>|null, isLoading: boolean,
 *   error: Error|null, summarize: (items: Array<object>) => object}}
 */
export function useMaterialSplitPreview(requests) {
  const inventoryIds = useMemo(() => collectInventoryIds(requests), [requests]);
  const idsKey = inventoryIds.join(',');

  const { data, isLoading, error } = useQuery({
    queryKey: ['material-split-stock', idsKey],
    enabled: inventoryIds.length > 0,
    staleTime: 30_000,
    queryFn: async () => {
      const { stockById, error: stockErr } = await fetchCurrentStock(inventoryIds);
      // `stockById === null` = la query no devolvió filas legibles. Se lanza para
      // que react-query lo exponga como error y la UI diga "no sé" en vez de
      // pintar un reparto sobre datos que no tiene.
      if (stockErr || !stockById) {
        throw new Error(stockErr?.message || 'No se pudo leer el stock actual.');
      }
      return stockById;
    },
  });

  // Sin ítems de catálogo no hay nada que consultar: Map vacío (todo se compra),
  // que NO es lo mismo que `null` (desconocido).
  const stockById = inventoryIds.length === 0
    ? EMPTY_STOCK
    : (data instanceof Map ? data : null);

  const summarize = useCallback(
    (items) => summarizeSplitPreview(items, stockById),
    [stockById],
  );

  return {
    stockById,
    isLoading: inventoryIds.length > 0 && isLoading,
    error: error || null,
    summarize,
  };
}

export default useMaterialSplitPreview;
